<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- ============================================================
                 HERO
                 ============================================================ -->
            <section class="mkt-hero-light mkt-hero-watermark" style="padding:80px 0 50px;">
                <div class="mkt-hero-watermark-icon mkt-hero-watermark-icon--right" aria-hidden="true">
                    <q-icon name="construction" />
                </div>
                <div class="mkt-container text-center" style="position:relative;z-index:1;">
                    <span class="mkt-trust-pill mkt-trust-pill--light q-mb-md" style="display:inline-block;">
                        <q-icon name="construction" class="mkt-trust-icon" /> For contractors, home services, trades
                    </span>
                    <h1 class="text-weight-bold q-mt-md q-mb-md" style="font-size:clamp(2rem,4vw,3rem);color:#0F172A;line-height:1.15;">
                        Job approved.<br>
                        <span class="text-gradient-indigo">Deposit collected.</span>
                        <span class="visually-hidden"> - Estimate signing and deposit collection for contractors</span>
                    </h1>
                    <p class="q-mb-lg" style="font-size:1.1rem;max-width:620px;margin:0 auto;color:#475569;">
                        Send the estimate. Customer signs and pays the down payment in the same flow.
                        Don't pull permits or buy materials until the money's in.
                    </p>
                    <div class="row justify-center q-gutter-md mkt-hero-cta-row">
                        <q-btn color="primary" size="lg" label="Start Free" href="/Account/Register?plan=collect"
                            no-caps unelevated icon-right="arrow_forward"
                            style="border-radius:10px;padding:10px 32px;font-weight:600;" />
                        <q-btn outline size="lg" no-caps label="See how it works" href="#workflow"
                            style="border-radius:10px;padding:10px 32px;color:#334155;border-color:#CBD5E1;" />
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 WORKFLOW
                 ============================================================ -->
            <section id="workflow" class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">How it works</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">From estimate to funded - without the back-and-forth</h2>
                    </div>
                    <div class="row q-col-gutter-xl items-stretch">
                        <div class="col-12 col-md-4" v-for="(step, i) in steps" :key="i">
                            <div class="mkt-step-tile">
                                <div class="mkt-step-ribbon">{{ String(i + 1).padStart(2, '0') }}</div>
                                <div class="row items-center q-gutter-sm q-mb-sm">
                                    <q-icon :name="step.icon" size="28px" color="primary" />
                                    <h3 class="text-weight-bold q-ma-none" style="font-size:1.2rem;color:#0a0f1a;line-height:1.2;">{{ step.title }}</h3>
                                </div>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.6;">{{ step.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 ROI CALLOUT
                 ============================================================ -->
            <section class="mkt-light reveal-fade-in" v-intersection="revealCfg" style="padding:50px 0;">
                <div class="mkt-container-narrow">
                    <div class="mkt-card q-pa-xl" style="border-left:4px solid var(--mkt-indigo);">
                        <div class="row items-center q-col-gutter-lg">
                            <div class="col-12 col-md-4 text-center">
                                <div class="text-weight-bold" style="font-size:3rem;letter-spacing:-0.02em;line-height:1;color:#0F172A;">
                                    <span style="background:linear-gradient(135deg,#B45309,#EF4444);-webkit-background-clip:text;background-clip:text;color:transparent;">$8,500</span>
                                </div>
                                <div class="text-grey-7 q-mt-xs" style="font-size:0.85rem;">deposit before materials hit the truck</div>
                            </div>
                            <div class="col-12 col-md-8">
                                <h3 class="text-weight-bold q-mb-sm" style="font-size:1.15rem;color:#0a0f1a;">Real flow</h3>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.7;">
                                    Monday - kitchen-remodel estimate sent.
                                    Tuesday - homeowner signs and the 50% deposit lands in your account.
                                    Wednesday - materials ordered. The job starts when the money's there,
                                    not when you remember to send a follow-up invoice.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 VERTICAL FEATURES
                 ============================================================ -->
            <section id="features" class="mkt-white reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Built for the trades</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">What you get</h2>
                    </div>
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-6 col-lg-4" v-for="f in features" :key="f.title">
                            <div class="mkt-card q-pa-lg" style="height:100%;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon :name="f.icon" size="32px" :style="`color:${f.color};`" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.05rem;color:#0a0f1a;line-height:1.25;">{{ f.title }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.9rem;line-height:1.6;">{{ f.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 PRICING
                 ============================================================ -->
            <section id="pricing" class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-lg">
                        <div class="mkt-section-eyebrow">Pricing</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Sign & Collect - built for jobsite signings</h2>
                        <p class="text-grey-7" style="font-size:1rem;max-width:560px;margin:0 auto;">
                            $39/mo for 50 signings, plus a 2.5% fee on collected deposits and progress payments.
                            Self-serve, no contract.
                        </p>
                    </div>
                    <div style="max-width:400px;margin:0 auto;">
                        <plan-card tier-code="collect"
                                   featured featured-label="For trades & home services"
                                   icon="payments" accented
                                   cta-label="Start with Sign & Collect"
                                   includes-label="What you get:" />
                    </div>
                    <div class="text-center q-mt-lg">
                        <span style="font-size:0.9rem;color:#475569;">Running 50+ jobs a month?</span>
                        <q-btn flat no-caps color="primary" label="See Pro plan"
                               icon-right="arrow_forward" href="/Home/SmallBusiness#plan-pro"
                               class="q-ml-sm"
                               style="font-size:0.9rem;font-weight:500;border-radius:8px;" />
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 FAQ
                 ============================================================ -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0 80px;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">FAQ</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Common questions from contractors</h2>
                    </div>
                    <q-list>
                        <q-expansion-item v-for="faq in faqs" :key="faq.q" :label="faq.q"
                            header-class="text-weight-bold" style="border-bottom:1px solid rgba(0,0,0,0.06);">
                            <q-card>
                                <q-card-section class="text-grey-7" style="font-size:0.95rem;line-height:1.7;">
                                    {{ faq.a }}
                                </q-card-section>
                            </q-card>
                        </q-expansion-item>
                    </q-list>
                </div>
            </section>

            <!-- ============================================================
                 FINAL CTA
                 ============================================================ -->
            <section class="mkt-cta-gradient reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:1.8rem;">Stop fronting materials before you're paid.</h2>
                    <p class="text-white q-mb-lg" style="opacity:0.9;font-size:1rem;">Self-serve. Secure card processing. Cancel anytime.</p>
                    <q-btn color="white" text-color="primary" size="lg" label="Start Free" href="/Account/Register?plan=collect"
                        no-caps unelevated icon-right="arrow_forward"
                        style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                </div>
            </section>

            <marketing-footer />
        </q-page-container>
    </q-layout>
</template>

<script>
import PlanCard from '@components/marketing/PlanCard.vue';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingFooter from '@components/marketing/MarketingFooter.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'ForContractorsPage',
    mixins: [revealMixin],
    components: { PlanCard, MarketingNav, MarketingFooter },
    data() {
        return {
            mobileNav: false,

            steps: [
                { icon: 'description', title: 'Send the estimate',
                    detail: 'Use a saved template (Customer/Contractor roles), drop in scope and totals, hit send. Works from the truck.' },
                { icon: 'edit',        title: 'Customer signs + pays the deposit',
                    detail: 'They approve the estimate in the browser, then drop in the down payment on the next screen. Mobile-friendly.' },
                { icon: 'local_shipping', title: 'Job starts on a funded basis',
                    detail: 'Deposit settles to your account. Materials get ordered. Permits pulled. No fronting cash before the job.' }
            ],

            features: [
                { icon: 'payments',         color: '#047857', title: 'Estimate + deposit in one flow',
                    detail: 'Customer signs the estimate and pays the down payment in the same browser session. One contract, one funded job start.' },
                { icon: 'account_balance',  color: '#3949ab', title: 'Instant Bank Payments on big jobs',
                    detail: 'Larger deposits often go by bank transfer to avoid card-fee bite. With Instant Bank Payments, funds settle the moment the customer submits — no waiting days for ACH before crews mobilize.' },
                { icon: 'event_repeat',     color: '#B45309', title: 'Multi-stage payments',
                    detail: 'Deposit → progress → final. Auth-hold or charge at each milestone. Clean record of what was paid against what was signed.' },
                { icon: 'edit_document',    color: '#2563EB', title: 'Change orders mid-project',
                    detail: 'Send a signed change order and collect the upcharge in the same flow. The amendment joins the original record.' },
                { icon: 'phone_iphone',     color: '#4F46E5', title: 'Jobsite-friendly signing',
                    detail: 'Mobile-friendly signing for in-truck or on-site approvals. No app install, no account required for the customer.' },
                { icon: 'palette',          color: '#7C3AED', title: 'Branded for your company',
                    detail: 'Your logo, brand colors, and reply-to address on the estimate, the signing ceremony, and the receipt the customer gets.' },
                { icon: 'verified',         color: '#0D9488', title: 'Tamper-evident records',
                    detail: 'Every signature and every payment captured with timestamp, IP, and SHA-256 hash. Solid evidence if a job ever goes sideways.' }
            ],

            faqs: [
                { q: 'Can I do progress payments (deposit, mid-job, final)?',
                    a: 'Yes. Set up the contract with the deposit at signing, then schedule auth-holds or capture-now charges at each progress milestone. Each payment event is captured against the same signed contract.' },
                { q: 'What about change orders mid-project?',
                    a: 'Send a signed change order from the dashboard. Customer signs and pays the upcharge in the same flow. The change order joins the audit trail of the original estimate.' },
                { q: 'How do you handle declined cards or NSF on a check from a customer?',
                    a: 'Cards: the signing flow surfaces the decline immediately so you don\'t mark the job as funded when it isn\'t. We don\'t process ACH/checks directly - all card payments go through our integrated payment processor and you can configure auth-hold or capture-now per job.' },
                { q: 'Can I use my own estimate template / branding?',
                    a: 'Yes - upload your existing estimate PDF or Word doc, pre-place signature and date fields, save as a template. On Pro and Business plans, your logo and brand colors appear throughout the signing flow.' },
                { q: 'What if the homeowner wants to negotiate before signing?',
                    a: 'Send a revised estimate at any point. Old version invalidates, new version becomes the source of truth. The audit trail tracks every revision sent.' },
                { q: 'Is the signed estimate legally binding?',
                    a: 'Yes - signatures are ESIGN Act and UETA compliant in all 50 states, with hash-chained audit trail and certificate of completion attached. Holds up the same as a wet-ink contract.' }
            ]
        };
    }
};
</script>

<style scoped>
.mkt-section-eyebrow {
    display: inline-block;
    font-size: 0.8rem;
    font-weight: 700;
    color: #4F46E5;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
}
</style>
